import { Box, Fade } from '@mui/material'
import { GroupModal } from 'components/groupModal/GroupModal'
import React, { useCallback, useRef, useState } from 'react'
import { AssignLocation } from './AssignLocation'
import { EquipmentDetails } from './EquipmentDetails'
import { useCurrentDeckContext } from 'components/currentDeckContext/CurrentDeckContext'
import { addScreen, updateDecks, updateScreen } from '../../common/data'
import { useSnackbar } from 'notistack'
import { useCurrentUser } from 'hooks'
import dayjs from 'dayjs'

const labels = ['Assign location', 'Equipment details']

export const CreateScreenGroupModal = ({
  locations,
  customers,
  sites,
  screens,
  equipments,
  setShowCreateScreenModal,
  data
}) => {
  const [activeStep, setActiveStep] = useState(0)
  const [assignLocationData, setAssignLocationData] = useState(data?.assignLocationData ?? {})
  const [equipmentDetailsData, setEquipmentDetailsData] = useState(data?.equipmentDetailsData ?? {})
  const [isCreating, setIsCreating] = useState(false)

  const assignLocationRef = useRef()
  const equipmentDetailsLocationRef = useRef()
  const { getTransactionMetaData } = useCurrentUser()

  const { enqueueSnackbar } = useSnackbar()
  const { setCustomerId, setSiteId, setLocationId, setEquipmentId, setDeckId } = useCurrentDeckContext()

  const onNextClicked = useCallback(() => {
    setActiveStep((currentStep) => {
      if (currentStep === 0) {
        const { error, values } = assignLocationRef.current.validate()
        if (error) {
          return currentStep
        }
        setAssignLocationData(values)
      }
      if (currentStep === 1) {
        return currentStep
      }
      return currentStep + 1
    })
  }, [])

  const handleScreenItemDialogClose = useCallback(
    async (equipmentDetailsData) => {
      try {
        setIsCreating(true)
        let equipmentId = assignLocationData.screen
        const existing = equipments.find((e) => e.id === data?.id)

        if (existing) {
          //Update Item
          updateScreen(
            {
              id: data.id,
              Name: assignLocationData.screen,
              LocationId: assignLocationData.location,
              CustomerId: assignLocationData.customer,
              ScreenId: equipmentDetailsData.screenId || null,
              SiteId: assignLocationData.site,
              DWGNumber: equipmentDetailsData.dwgNumber,
              SchenckSerial: equipmentDetailsData.sandvikSerialNumber,
              SiteSerial: equipmentDetailsData.siteSerialNumber,
              Exciters: equipmentDetailsData.exciters,
              DateInstalled: dayjs(equipmentDetailsData.dateInstalled).toISOString(),
              ScreenTonnage: equipmentDetailsData.screenTonnage,
              ScreenCapacity: equipmentDetailsData.screenCapacity,
              MaintenanceFrequency: equipmentDetailsData.maintenanceFrequency,
              LastUpdatedBy: getTransactionMetaData()
            },
            getTransactionMetaData()
          ).then(() => {
            updateDecks(existing.Name, equipmentId, equipmentDetailsData.screenId, getTransactionMetaData()).then(
              () => {
                enqueueSnackbar(`Screen updated successfully`)
              }
            )
          })
        } else {
          //Add Item
          addScreen(
            {
              Name: assignLocationData.screen,
              LocationId: assignLocationData.location,
              CustomerId: assignLocationData.customer,
              ScreenId: equipmentDetailsData.screenId || null,
              SiteId: assignLocationData.site,
              DWGNumber: equipmentDetailsData.dwgNumber,
              SchenckSerial: equipmentDetailsData.sandvikSerialNumber,
              SiteSerial: equipmentDetailsData.siteSerialNumber,
              Exciters: equipmentDetailsData.exciters,
              DateInstalled: dayjs(equipmentDetailsData.dateInstalled).toISOString(),
              ScreenTonnage: equipmentDetailsData.screenTonnage,
              ScreenCapacity: equipmentDetailsData.screenCapacity,
              MaintenanceFrequency: equipmentDetailsData.maintenanceFrequency,
              LastUpdatedBy: getTransactionMetaData()
            },
            getTransactionMetaData()
          ).then((newScreen) => {
            equipmentId = newScreen.Name
            updateDecks(data?.id, equipmentId, equipmentDetailsData.screenId, getTransactionMetaData()).then(() => {
              enqueueSnackbar(`Added ${newScreen.Name}`)
            })
          })
        }

        setCustomerId(assignLocationData.customer)
        setSiteId(assignLocationData.site)
        setLocationId(assignLocationData.location)
        setEquipmentId(equipmentId)
        setDeckId('')
        setShowCreateScreenModal({
          show: false
        })
      } catch (error) {
        enqueueSnackbar(`An error occurred whilst attempting to save`, {
          variant: 'error'
        })
      } finally {
        setIsCreating(false)
      }
    },
    [assignLocationData, getTransactionMetaData, data]
  )

  const onPreviousClicked = useCallback(() => {
    setActiveStep((currentStep) => {
      if (currentStep === 0) {
        return
      }
      if (currentStep === 1) {
        const { values } = equipmentDetailsLocationRef.current.validate()

        setEquipmentDetailsData(values)
      }
      return currentStep - 1
    })
  }, [])

  const onCreateClicked = useCallback(() => {
    const { error, values } = equipmentDetailsLocationRef.current.validate()
    if (error) {
      return
    }
    setEquipmentDetailsData(values)
    setTimeout(() => {
      handleScreenItemDialogClose(values)
    }, 500)
  }, [handleScreenItemDialogClose])

  const onCancelClicked = useCallback(() => {
    setShowCreateScreenModal({
      show: false
    })
  }, [])

  return (
    <GroupModal
      activeStep={activeStep}
      nextForm="assign_location"
      labels={labels}
      onPreviousClicked={onPreviousClicked}
      onNextClicked={onNextClicked}
      onCancelClicked={onCancelClicked}
      open
      canCreate={activeStep === 1}
      onCreateClicked={onCreateClicked}
      isCreating={isCreating}
      submitButtonTitle={data?.id ? (isCreating ? 'Updating' : 'Update') : isCreating ? 'Creating' : 'Create'}
      title={data?.id ? 'Update Screen' : 'Create new screen'}>
      <Box mt={2}>
        {activeStep === 0 && (
          <Fade timeout={1000} in>
            <div>
              <AssignLocation
                data={assignLocationData}
                ref={assignLocationRef}
                locations={locations}
                customers={customers}
                sites={sites}
              />
            </div>
          </Fade>
        )}
        {activeStep === 1 && (
          <Fade timeout={1000} in>
            <div>
              <EquipmentDetails data={equipmentDetailsData} screens={screens} ref={equipmentDetailsLocationRef} />
            </div>
          </Fade>
        )}
      </Box>
    </GroupModal>
  )
}
